import { ParsedQuery, LabelValue } from "../types";

/**
 * Parse a LogQL query string into structured components
 */
export function parseQuery(query: string): ParsedQuery {
  const result: ParsedQuery = { labels: {}, pipeline: "" };

  if (!query || query.trim() === "") {
    return result;
  }

  const trimmed = query.trim();

  // Find the labels section (between { and })
  const braceStart = trimmed.indexOf("{");
  const braceEnd = trimmed.indexOf("}");

  if (braceStart === -1 || braceEnd === -1) {
    return result;
  }

  const labelsStr = trimmed.slice(braceStart + 1, braceEnd);
  const pipelineStr = trimmed.slice(braceEnd + 1).trim();

  if (pipelineStr) {
    result.pipeline = pipelineStr;
  }

  if (!labelsStr.trim()) {
    return result;
  }

  // Parse labels - handle comma separation while respecting quoted values
  const labelPairs = splitLabels(labelsStr);

  for (const pair of labelPairs) {
    const parsed = parseLabelPair(pair.trim());
    if (parsed) {
      result.labels[parsed.key] = parsed.value;
    }
  }

  return result;
}

/**
 * Split labels string by commas, respecting quoted values
 */
function splitLabels(labelsStr: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  let escapeNext = false;

  for (let i = 0; i < labelsStr.length; i++) {
    const char = labelsStr[i];

    if (escapeNext) {
      current += char;
      escapeNext = false;
      continue;
    }

    if (char === "\\") {
      current += char;
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (char === "," && !inQuotes) {
      if (current.trim()) {
        result.push(current.trim());
      }
      current = "";
      continue;
    }

    current += char;
  }

  if (current.trim()) {
    result.push(current.trim());
  }

  return result;
}

/**
 * Parse a single label pair like 'app="nginx"' or 'app=~"nginx.*"'
 */
function parseLabelPair(pair: string): { key: string; value: LabelValue } | null {
  // Match patterns: key="value", key=~"value", key!="value", key!~"value"
  const patterns = [
    { regex: /^(\w+)\s*=~\s*"((?:[^"\\]|\\.)*)"$/, type: "regex" as const },
    { regex: /^(\w+)\s*!~\s*"((?:[^"\\]|\\.)*)"$/, type: "notRegex" as const },
    { regex: /^(\w+)\s*!=\s*"((?:[^"\\]|\\.)*)"$/, type: "notEqual" as const },
    { regex: /^(\w+)\s*=\s*"((?:[^"\\]|\\.)*)"$/, type: "equal" as const },
  ];

  for (const { regex, type } of patterns) {
    const match = pair.match(regex);
    if (match) {
      const key = match[1];
      const rawValue = match[2].replace(/\\"/g, '"');

      if (type === "equal") {
        return { key, value: rawValue };
      } else if (type === "regex") {
        return { key, value: { regex: rawValue } };
      } else if (type === "notEqual") {
        return { key, value: { notEqual: rawValue } };
      } else if (type === "notRegex") {
        return { key, value: { notRegex: rawValue } };
      }
    }
  }

  return null;
}

/**
 * Build a LogQL query string from structured components
 */
export function buildQuery(parsed: ParsedQuery): string {
  const labelParts: string[] = [];

  for (const [key, value] of Object.entries(parsed.labels)) {
    if (typeof value === "string") {
      labelParts.push(`${key}="${value}"`);
    } else if ("regex" in value) {
      labelParts.push(`${key}=~"${value.regex}"`);
    } else if ("notEqual" in value) {
      labelParts.push(`${key}!="${value.notEqual}"`);
    } else if ("notRegex" in value) {
      labelParts.push(`${key}!~"${value.notRegex}"`);
    }
  }

  const labelsStr = labelParts.join(", ");
  const query = `{${labelsStr}}`;

  if (parsed.pipeline) {
    return `${query} ${parsed.pipeline}`;
  }

  return query;
}

/**
 * Get a label value from a query string (only returns simple string values)
 */
export function getQueryLabel(query: string, key: string): string | null {
  const parsed = parseQuery(query);
  const value = parsed.labels[key];

  if (typeof value === "string") {
    return value;
  }

  return null;
}

/**
 * Set a label value in a query string
 */
export function setQueryLabel(query: string, key: string, value: string): string {
  const parsed = parseQuery(query);
  parsed.labels[key] = value;
  return buildQuery(parsed);
}

/**
 * Remove a label from a query string
 */
export function removeQueryLabel(query: string, key: string): string {
  const parsed = parseQuery(query);
  delete parsed.labels[key];
  return buildQuery(parsed);
}

/**
 * Check if a query can be safely parsed.
 * Returns false if the query appears to have content that couldn't be parsed
 * (e.g., unclosed quotes, invalid syntax).
 */
export function isQueryParseable(query: string): boolean {
  if (!query || query.trim() === "") {
    return true; // Empty query is valid
  }

  const trimmed = query.trim();
  const braceStart = trimmed.indexOf("{");
  const braceEnd = trimmed.indexOf("}");

  // No braces at all - not a LogQL query format
  if (braceStart === -1 && braceEnd === -1) {
    return false;
  }

  // Mismatched braces
  if (braceStart === -1 || braceEnd === -1 || braceEnd < braceStart) {
    return false;
  }

  const labelsStr = trimmed.slice(braceStart + 1, braceEnd).trim();

  // Empty labels section is valid: {}
  if (!labelsStr) {
    return true;
  }

  // Check for unclosed quotes
  let inQuotes = false;
  let escapeNext = false;
  for (const char of labelsStr) {
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (char === "\\") {
      escapeNext = true;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
    }
  }

  // If we end with unclosed quotes, query is invalid
  if (inQuotes) {
    return false;
  }

  return true;
}
